import { Component, EventEmitter, Input, NgZone, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as JSZip from 'jszip';
import { MessageService } from 'primeng/api';
import { filter, firstValueFrom, switchMap } from 'rxjs';
import { EXPORT_OPTIONS, TEMPLATE_EXPORT_TYPES } from 'src-ironside/constants/export-product';
import { ProductExtendInfo } from 'src-ironside/repositories/product.repository';
import { CommonService } from 'src-ironside/services/common.service';
import { PermissionService } from 'src-ironside/services/permission.service';
import { SessionStorageService } from 'src-ironside/services/session-storage.service';
import { downloadAllResource } from 'src-ironside/utils/dowload-file-zip';
import { DynamicDocumentService } from 'src/app/shared/services/dynamic-document.service';
import { environment } from 'src/environments/environment.ironside';

@Component({
    selector: 'app-export-data-product',
    templateUrl: './export-data-product.component.html',
    styleUrls: ['./export-data-product.component.css'],
})
export class ExportDataProductComponent implements OnInit {
    @Input() disabledBtnImport: boolean = false; /* !products?.length */
    @Input() products: any[] = [];
    @Input() idParentProduct: string = '';
    @Input() idProductSelected: string[] = [];
    @Input() exportOption: any[] = EXPORT_OPTIONS;
    @Input() height: number = 0;

    loadingExport: boolean = false;
    exportVisible: boolean = false;
    itemExportSelected: any[] = [];
    isProgress: boolean = false;
    progressNumber: number = 0;
    interval: any;
    isExportCancelled: boolean = false;
    finalZipBlob: Blob | null = null;

    constructor(
        public sessionStorageService: SessionStorageService,
        private ngZone: NgZone,
        private _messageService: MessageService,
        private translate: TranslateService,
        private _commonService: CommonService,
        private dynamicDocumentService: DynamicDocumentService,
        public permissionService: PermissionService,
        private activatedRoute: ActivatedRoute
    ) {}

    ngOnInit(): void {}

    /**
     * Lấy ID của product có referenceProduct khớp với URL
     * URL có dạng: product/detail/{referenceProduct}
     */
    getProductIdsByReferenceProduct(products: ProductExtendInfo[]): string[] {
        // Lấy referenceProduct từ URL params
        const routeParams = this.activatedRoute.snapshot.params;
        const referenceProductFromUrl = routeParams['id']; // referenceProduct từ URL

        if (!referenceProductFromUrl) {
            // Nếu không có trong URL, fallback về cách cũ
            return products
                .map((p: any) => p.id)
                .filter((id: string | undefined): id is string => Boolean(id));
        }

        // Tìm product có referenceProduct khớp với URL
        const matchedProducts = products.filter((product: any) => {
            return product.referenceProduct === referenceProductFromUrl;
        });

        // Lấy IDs của các product khớp
        const productIds = matchedProducts
            .map((p: any) => p.id)
            .filter((id: string | undefined): id is string => Boolean(id));

        // Nếu không tìm thấy, fallback về cách cũ
        if (productIds.length === 0) {
            return products
                .map((p: any) => p.id)
                .filter((id: string | undefined): id is string => Boolean(id));
        }

        return productIds;
    }

    /**
     * Load PHOTO resources from API to get hd field
     * Không transform data để giữ nguyên giá trị hd
     */
    async loadPhotoResourcesWithHd(productIds: string[]): Promise<any[]> {
        try {
            if (!productIds || productIds.length === 0) {
                return [];
            }

            const currentLocale =
                environment.languagesExtranet.find(
                    (lang) => lang.code === this.translate.currentLang
                )?.locale || 'en_GB';

            const apiResponse = await firstValueFrom(
                this.dynamicDocumentService.listResourcesByProductIds(
                    [this.idParentProduct],
                    ['PHOTO'],
                    [currentLocale],
                    false
                )
            );

            // Không transform data để giữ nguyên giá trị hd
            return Array.isArray(apiResponse) ? apiResponse : apiResponse?.content || [];
        } catch (error) {
            console.error('Error loading PHOTO resources:', error);
            return [];
        }
    }

    updateExportPermission(fonctions: string[], ref: string, key: string): void {
        const hasPermission = fonctions?.includes(ref) ?? true;
        this.exportOption.forEach((el) => {
            if (el.key === key) {
                el.permission = hasPermission;
            }
        });
    }

    onShowTypeExport() {
        if (
            (this.products?.[0]?.length ?? 0) > 0 &&
            this.exportOption.some((el) => el?.permission === true)
        ) {
            this.exportVisible = !this.exportVisible;
        }
    }

    isDisabled(key: string): boolean {
        return this.itemExportSelected?.length >= 3 && !this.itemExportSelected.includes(key);
    }

    onCancelExport() {
        this.isExportCancelled = true;
        this.exportVisible = false;
        this.loadingExport = false;
        this.isProgress = false;
        this.progressNumber = 0;
        this.itemExportSelected = [];
        this.finalZipBlob = null;
        clearInterval(this.interval);
    }

    async onExportResource() {
        this.isExportCancelled = false;
        this.loadingExport = true;
        this.isProgress = true;
        this.progressExport();

        // Check if we have any template-based exports
        const templateExports = this.itemExportSelected.filter((type) =>
            TEMPLATE_EXPORT_TYPES.includes(type)
        );
        const regularExports = this.itemExportSelected.filter(
            (type) => !TEMPLATE_EXPORT_TYPES.includes(type)
        );

        const mappedArrays = regularExports.reduce((acc, key) => {
            acc[key] = [];
            return acc;
        }, {} as Record<string, any[]>);

        if (regularExports.length > 0) {
            const selectedProducts =
                this.idProductSelected.length > 0
                    ? this.products?.[0]?.filter((product: any) =>
                          this.idProductSelected.includes(product.id)
                      ) || []
                    : this.products?.[0] || [];

            // Lấy resources từ fullExtendData cho các type khác PHOTO
            const resource = selectedProducts
                .flatMap((product: ProductExtendInfo) => product.resources || [])
                .filter((item: any) => item?.files?.length);

            // Kiểm tra có export PHOTO hoặc PHOTO_THUMBNAIL không
            const hasPhotoExport =
                regularExports.includes('PHOTO') || regularExports.includes('PHOTO_THUMBNAIL');

            let photoResourcesFromApi: any[] = [];

            // Nếu có export PHOTO, gọi API để lấy resources có hd
            if (hasPhotoExport) {
                // Lấy product IDs dựa trên referenceProduct từ URL
                const productIds = this.getProductIdsByReferenceProduct(selectedProducts);

                if (productIds.length > 0) {
                    photoResourcesFromApi = await this.loadPhotoResourcesWithHd(productIds);
                }
            }

            regularExports.forEach((item) => {
                if (item === 'PHOTO') {
                    // PHOTO (HR): chỉ lấy từ API khi hd === true
                    mappedArrays.PHOTO = photoResourcesFromApi
                        .filter((res: any) => {
                            const resType =
                                res?.typeOfResourceInfo?.valeur ||
                                res?.typeOfResource?.valeur ||
                                res?.typeOfResource?.value ||
                                res?.typeOfResource;
                            if (resType !== 'PHOTO') {
                                return false;
                            }
                            const files = res?.file || res?.files || [];
                            const firstFile = files[0];
                            const isHd = firstFile?.hd === true;
                            return isHd && firstFile?.url;
                        })
                        .map((res: any) => {
                            // Normalize format
                            const files = res?.file || res?.files || [];
                            return {
                                ...res,
                                typeOfResource: 'PHOTO',
                                files: files.map((file: any) => ({
                                    id: file?.id || file?.resourceFileId,
                                    name: file?.name,
                                    url: file?.url,
                                    thumbnailUrl: file?.thumbnailUrl,
                                    hd: file?.hd,
                                })),
                            };
                        });
                } else if (item === 'PHOTO_THUMBNAIL') {
                    // PHOTO_THUMBNAIL (LR): lấy tất cả PHOTO từ API, không cần kiểm tra hd
                    mappedArrays.PHOTO_THUMBNAIL = photoResourcesFromApi
                        .filter((res: any) => {
                            const resType =
                                res?.typeOfResourceInfo?.valeur ||
                                res?.typeOfResource?.valeur ||
                                res?.typeOfResource?.value ||
                                res?.typeOfResource;
                            const files = res?.file || res?.files || [];
                            return resType === 'PHOTO' && files?.[0]?.url;
                        })
                        .map((res: any) => {
                            // Normalize format và dùng thumbnailUrl
                            const files = res?.file || res?.files || [];
                            return {
                                ...res,
                                typeOfResource: 'PHOTO',
                                files: files.map((file: any) => ({
                                    id: file?.id || file?.resourceFileId,
                                    name: file?.name,
                                    url: file?.thumbnailUrl || file?.url,
                                    thumbnailUrl: file?.thumbnailUrl,
                                    hd: file?.hd,
                                })),
                            };
                        });
                } else {
                    // Các type khác: dùng resources từ fullExtendData
                    mappedArrays[item] = resource?.filter((el: any) => el?.typeOfResource === item);
                }
            });
        }

        // Create array of promises for both template and regular exports
        const exportPromises = [
            // Add template export promises
            ...templateExports.map((type) =>
                this.handleTemplateExport(type).catch((error) => {
                    const exportOption = this.exportOption.find((el) => el?.key === type);
                    this._messageService.add({
                        severity: 'error',
                        detail: this.translate.instant('export.DOWNLOAD_ERROR', {
                            item: exportOption?.label,
                        }),
                    });
                    throw error;
                })
            ),
            // Add regular export promises
            ...Object.entries(mappedArrays).map(([_, resources], index) => {
                return downloadAllResource(resources as any).catch((error) => {
                    const exportOption = this.exportOption.find(
                        (el) => el?.key === Object.keys(mappedArrays)[index]
                    );
                    this._messageService.add({
                        severity: 'error',
                        detail: this.translate.instant('export.DOWNLOAD_ERROR', {
                            item: exportOption?.label,
                        }),
                    });
                    throw error;
                });
            }),
        ];

        Promise.allSettled(exportPromises)
            .then((results) => {
                if (this.isExportCancelled) return;

                const finalZip = new JSZip();
                let successCount = 0;
                let errorCount = 0;

                results.forEach((result, index) => {
                    const exportType =
                        index < templateExports.length
                            ? templateExports[index]
                            : Object.keys(mappedArrays)[index - templateExports.length];

                    const exportOption = this.exportOption.find((el) => el?.key === exportType);

                    if (result.status === 'fulfilled' && result.value) {
                        if (TEMPLATE_EXPORT_TYPES.includes(exportType)) {
                            finalZip.file(
                                `${this.translate.instant('export.' + exportOption?.label)}.${
                                    exportType.includes('CATALOGUE') ||
                                    exportType.includes('CATALOGUE_NO_PRICE') ||
                                    exportType.includes('CATALOGUE_DATA_SHEET')
                                        ? 'pdf'
                                        : 'xlsx'
                                }`,
                                result.value
                            );
                        } else {
                            finalZip.file(
                                `${this.translate.instant('export.' + exportOption?.label)}.zip`,
                                result.value
                            );
                        }
                        successCount++;
                    } else {
                        errorCount++;
                    }
                });

                this.progressNumber = 100;
                clearInterval(this.interval);

                if (this.isExportCancelled) return;

                if (successCount === 0) {
                    this.loadingExport = false;
                    this.isProgress = false;
                    this.progressNumber = 0;
                    return;
                }

                return finalZip.generateAsync({ type: 'blob' });
            })
            .then((finalZipBlob) => {
                if (this.isExportCancelled || !finalZipBlob) return;

                this.finalZipBlob = finalZipBlob;
                this.loadingExport = false;

                if (this.progressNumber >= 100) {
                    this.onExport(this.finalZipBlob);
                }
            })
            .catch((error) => {
                this.loadingExport = false;
                this.isProgress = false;
                this.progressNumber = 0;
                clearInterval(this.interval);
            });
    }

    progressExport() {
        this.ngZone.runOutsideAngular(() => {
            this.interval = setInterval(() => {
                this.ngZone.run(() => {
                    if (this.loadingExport) {
                        this.progressNumber = Math.min(
                            this.progressNumber + Math.floor(Math.random() * 10) + 8,
                            99
                        );
                    } else {
                        this.progressNumber = Math.min(
                            this.progressNumber + Math.floor(Math.random() * 10) + 8,
                            100
                        );
                        if (this.progressNumber >= 100) {
                            if (this.finalZipBlob) {
                                this.onExport(this.finalZipBlob);
                            }
                        }
                    }
                });
            }, 400);
        });
    }

    onExport(finalZipBlob: Blob) {
        let nameFile = '';
        if (this.itemExportSelected?.length > 1) {
            this.itemExportSelected?.forEach((item) => {
                const exportOption = this.exportOption.find((el) => el?.key === item);
                if (exportOption) {
                    nameFile += this.translate.instant('export.' + exportOption.label) + '_';
                }
            });
        } else if (this.itemExportSelected?.length === 1) {
            const exportOption = this.exportOption.find(
                (el) => el?.key === this.itemExportSelected[0]
            );
            if (exportOption) {
                nameFile += this.translate.instant('export.' + exportOption.label);
            }
        }
        const zipUrl = URL.createObjectURL(finalZipBlob);
        const a = document.createElement('a');
        a.href = zipUrl;
        a.download = `${nameFile}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(zipUrl);
        this.isProgress = false;
        this.progressNumber = 0;
        clearInterval(this.interval);
    }

    private handleTemplateExport(exportTypes: string): Promise<Blob> {
        return new Promise((resolve) => {
            this._commonService
                .getCategories()
                .pipe(
                    filter((el: any) => el && el.length > 0),
                    switchMap((el: any) => {
                        const conditions = this.idProductSelected.map((item) => {
                            return {
                                type: 'EQUALS',
                                value: item,
                            };
                        });
                        let filter: any[] = [
                            {
                                fieldName: 'linkToParentProduct.documentId',
                                conditions: [
                                    {
                                        type: 'EQUALS',
                                        value: this.idParentProduct,
                                    },
                                ],
                            },
                            {
                                fieldName: 'childProduct.documentId',
                                conditions: conditions,
                            },
                        ];

                        let templateTypes;
                        switch (exportTypes) {
                            case 'CATALOGUE':
                                templateTypes = 'EXPORT_PRODUCT_PRICES';
                                break;
                            case 'CATALOGUE_NO_PRICE':
                                templateTypes = 'EXPORT_PRODUCT_WITHOUT_PRICES';
                                break;
                            case 'CATALOGUE_DATA_SHEET':
                                templateTypes = 'EXPORT_PRODUCT_LOGISTICS';
                                break;
                            case 'PRICE':
                                if (
                                    this.permissionService.isPermissionFonction('visibilite-tarifs')
                                ) {
                                    templateTypes = 'EXPORT_PRODUCT_PRICES';
                                } else {
                                    templateTypes = 'EXPORT_PRODUCT_WITHOUT_PRICES';
                                }
                                break;
                            case 'LOGISTIC_DATA':
                                templateTypes = 'EXPORT_PRODUCT_LOGISTICS';
                                break;
                        }

                        const body = {
                            filters: filter,
                            fields: [
                                'id',
                                'longDescription',
                                'shortDescription',
                                'brand',
                                'packaging',
                                'state',
                                'referenceProduct',
                                'countryOfManufacture',
                                'typeOfProduct',
                                'creationDate',
                                'department',
                                'lastModifiedUser',
                                'statusProduct',
                                'newStartDate',
                                'newEndDate',
                            ],
                            locales: [
                                environment.languagesExtranet.find(
                                    (lang) => lang.code === this.translate.currentLang
                                )?.locale || 'en_GB',
                            ],
                            sorts: {},
                            page: 1,
                            pageSize: 100,
                            exportType: templateTypes,
                            /*  hideFields: hideFields, */
                        };

                        const templateType =
                            exportTypes.includes('CATALOGUE') ||
                            exportTypes.includes('CATALOGUE_NO_PRICE') ||
                            exportTypes.includes('CATALOGUE_DATA_SHEET')
                                ? 'pdf'
                                : 'xlsx';

                        return firstValueFrom(
                            this.dynamicDocumentService.exportProductByTemplate(body, templateType)
                        );
                    })
                )
                .subscribe({
                    next: (res) => {
                        resolve(res);
                    },
                    error: (error) => {
                        const exportOption = this.exportOption.find(
                            (el) => el?.key === exportTypes
                        );
                        this._messageService.add({
                            severity: 'error',
                            detail: this.translate.instant('export.FILE_EXPORT_ERROR', {
                                type: this.translate.instant('export.' + exportOption?.label),
                            }),
                        });
                        resolve(null as any);
                    },
                });
        });
    }
}
